import { useState, useRef, useCallback } from 'react';
import dynamic from 'next/dynamic';
import Icon from '@/components/shared/Icons';
import type { InputProps, SearchBarFilter, SearchBarProps } from '@/components/search-bar/SearchBar';
import styles from '@/components/search-bar/SearchBar.styles';
import MobileSearchInput from '@/components/search-bar/MobileSearchInput';
import GuestRoomContent from '@/components/search-bar/GuestRoomContent';

const Popper = dynamic(() => import('@/components/shared/popper/Popper'), { ssr: false });
const Button = dynamic(() => import('@/components/shared/button/Button'), { ssr: false });

export default function MobileSearchBar({
  searchBarFilters,
  labels,
  selectedRange,
  handleRangeSelect,
  locale,
  submitHandler,
  setUserModifiedGRDate,
  isGuestRoomDatePickerOpen,
  setIsGuestRoomDatePickerOpen
}: SearchBarProps): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [showGuestRoomDetails, setShowGuestRoomDetails] = useState(false);
  const triggerRef = useRef<HTMLInputElement>(null);
  const togglePopper = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);
  const submitSearchRequest = useCallback(
    e => {
      const showErrorMessage = searchBarFilters.some(filter => filter.showErrorMessage);
      if (!showErrorMessage) {
        setIsOpen(!isOpen);
        submitHandler(e);
      }
    },
    [isOpen, searchBarFilters, submitHandler]
  );

  const {
    inputType,
    id,
    ref,
    showErrorMessage,
    name,
    inputValue,
    onChange,
    label,
    blockedDays,
    maxRange,
    guestRoomDetails
  } = searchBarFilters[4];
  const guestRoomInputProps: InputProps = {
    type: inputType,
    id,
    ref,
    className: styles.mobileTextBox(showErrorMessage),
    name,
    value: inputValue,
    onChange,
    placeholder: label,
    'aria-label': label,
    max: maxRange
  };

  // prepare formatted search criteria
  const getSearchText = useCallback((filters: SearchBarFilter[]): string => {
    let searchText = '';
    if (filters[0].inputValue) {
      searchText += filters[0].inputValue;
    }
    if (filters[1].inputValue) {
      searchText += `, ${filters[1].inputValue}`;
    }
    if (filters[2].inputValue) {
      searchText += ` - ${filters[2].inputValue}`;
    }
    if (filters[3].inputValue) {
      searchText += `, ${filters[3].inputValue} ${filters[3].label}`;
    }
    if (filters[4].inputValue) {
      searchText += `, ${filters[4].inputValue} ${filters[4].label}`;
    }
    return searchText;
  }, []);

  const toggleGuestRoomDetails = useCallback(() => {
    setShowGuestRoomDetails(!showGuestRoomDetails);
  }, [showGuestRoomDetails]);

  return (
    <>
      <div className="flex justify-between gap-4 lg:hidden">
        <div className="relative w-full">
          <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
            <Icon id="search" className="h-6 w-6 fill-neutral-40" />
          </div>
          <input
            type="text"
            className={styles.mobileSearchBox}
            onClick={togglePopper}
            ref={triggerRef}
            value={getSearchText(searchBarFilters)}
            aria-label={labels.searchVenues}
            readOnly
          />
        </div>
      </div>
      {isOpen && (
        <Popper isOpen={isOpen} hasShadow>
          <div className="relative flex h-auto w-screen flex-col rounded-lg bg-background p-4 shadow-2 lg:hidden">
            <span className="text-neutral-60">{labels.eventDetails}</span>

            {/* location */}
            <div className="mt-2">
              <MobileSearchInput
                filter={searchBarFilters[0]}
                textBoxClassNames={styles.mobileTextBox(searchBarFilters[0].showErrorMessage)}
                isSearchInput
              />
            </div>

            {/* event start and event end */}
            <div className="flex gap-4">
              <MobileSearchInput
                filter={searchBarFilters[1]}
                textBoxClassNames={styles.mobileTextBox(searchBarFilters[1].showErrorMessage)}
                selectedRange={selectedRange}
                handleRangeSelect={handleRangeSelect}
                locale={locale}
                dateRangeAriaLabels={{
                  dialogAriaLabel: labels.dialog,
                  prevButtonAriaLabel: labels.previousMonth,
                  nextButtonAriaLabel: labels.nextMonth,
                  guestRoomPopperResetDatesLabel: labels.guestRoomPopperResetDatesLabel,
                  startDate: labels.startDate,
                  endDate: labels.endDate
                }}
              />
              <MobileSearchInput
                filter={searchBarFilters[2]}
                textBoxClassNames={styles.mobileTextBox(searchBarFilters[2].showErrorMessage)}
                selectedRange={selectedRange}
                handleRangeSelect={handleRangeSelect}
                locale={locale}
                dateRangeAriaLabels={{
                  dialogAriaLabel: labels.dialog,
                  prevButtonAriaLabel: labels.previousMonth,
                  nextButtonAriaLabel: labels.nextMonth,
                  guestRoomPopperResetDatesLabel: labels.guestRoomPopperResetDatesLabel,
                  startDate: labels.startDate,
                  endDate: labels.endDate
                }}
              />
            </div>

            {/* attendees and guest rooms */}
            <div className="flex gap-4">
              <MobileSearchInput
                filter={searchBarFilters[3]}
                textBoxClassNames={styles.mobileTextBox(searchBarFilters[3].showErrorMessage)}
              />
              <MobileSearchInput
                filter={{ ...searchBarFilters[4], onClick: toggleGuestRoomDetails }}
                textBoxClassNames={styles.mobileTextBox(searchBarFilters[4].showErrorMessage)}
              />
            </div>
            {showGuestRoomDetails ? (
              <div>
                <GuestRoomContent
                  datePickerOpen={isGuestRoomDatePickerOpen}
                  setDatePickerOpen={setIsGuestRoomDatePickerOpen}
                  inputProps={guestRoomInputProps}
                  labels={labels}
                  blockedDays={blockedDays}
                  guestRoomDetails={guestRoomDetails}
                  locale={locale}
                  toggleGuestRoomDetails={toggleGuestRoomDetails}
                  setUserModifiedGRDate={setUserModifiedGRDate}
                />
              </div>
            ) : null}
            <Button
              type="button"
              aria-label={labels.searchVenues}
              onClick={submitSearchRequest}
              text={labels.searchVenues}
              icon={<Icon id="search" className="h-4 w-4 fill-background" />}
              appearance="filled"
            />

            <div className="absolute right-0 top-0 p-2">
              <Button
                type="button"
                onClick={togglePopper}
                aria-label={labels.close}
                variant="neutral-white"
                appearance="ghost"
                icon={<Icon id="x" className="h-6 w-6" />}
              />
            </div>
          </div>
        </Popper>
      )}
    </>
  );
}
